import { ChangeEvent, memo, useCallback, useMemo, useState } from "react";
import { clsx } from "clsx";

import { fetchPaymentMethods, PaymentMethods } from "@/entities/Payment";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { handleError } from "@/shared/lib/helpers/utils";
import { useAppDispatch } from "@/shared/lib/hooks/redux";
import { PaymentFormErrors } from "@/shared/types";
import Button from "@/shared/ui/Button/Button";
import { Label } from "@/shared/ui/Label";
import { Title, TitleSize } from "@/shared/ui/Title";
import { addPaymentMethod } from "@/shared/utils/api";
import {
  cardNumberInputPattern,
  cvcInputPattern,
  expDateInputPattern,
  validatePaymentMethod,
  zipCodeGlobalPattern,
} from "@/shared/utils/validation";

import TextFormInput from "../../../../shared/ui/TextFormInput";

import cls from "./AddPaymentForm.module.scss";

type AddPaymentFormProps = {
  className?: string;
  userId?: string;
  onUpdate?: (data: PaymentMethods) => void;
  onError?: (error: unknown) => void;
  buttonText?: string;
  makeDefault?: boolean;
};

const defaultCardInfo = {
  expDate: "",
  cvc: "",
  cardNumber: "",
  cardHolder: "",
  cardZipcode: "",
};

export const AddPaymentForm = memo((props: AddPaymentFormProps) => {
  const { className, userId, onUpdate, onError, buttonText, makeDefault } = props;

  const dispatch = useAppDispatch();
  const { showAlert } = useAlert();

  const defaultFormValues = useMemo(() => (userId ? { ...defaultCardInfo, userId } : defaultCardInfo), [userId]);

  const [addPaymentForm, setAddPaymentForm] = useState(defaultFormValues);
  const [paymentFormErrors, setPaymentFormErrors] = useState<PaymentFormErrors>({});
  const [submittingPayment, setSubmittingPayment] = useState<boolean>(false);

  const formProps = {
    model: addPaymentForm,
    changeModel: setAddPaymentForm,
    errors: paymentFormErrors,
  };

  const onSubmit = useCallback(
    async (e: ChangeEvent<HTMLFormElement>) => {
      e.preventDefault();

      const errors = validatePaymentMethod(addPaymentForm);
      setPaymentFormErrors(errors);

      if (Object.keys(errors).length) {
        return;
      }

      setSubmittingPayment(true);

      const { success, data, error } = await addPaymentMethod(addPaymentForm, makeDefault);

      setSubmittingPayment(false);

      if (success) {
        if (onUpdate) {
          onUpdate(data);
        }

        dispatch(fetchPaymentMethods());

        setAddPaymentForm(defaultFormValues);
      } else {
        if (onError) {
          onError(error);
        }

        const err = handleError(error);

        showAlert({
          theme: AlertTheme.ERROR,
          message: err.details,
        } as AlertOptions);
      }
    },
    [dispatch, onUpdate, onError, makeDefault, showAlert, addPaymentForm, defaultFormValues]
  );

  return (
    <form className={clsx(cls.AddPaymentForm, {}, [className])} onSubmit={onSubmit}>
      <Title size={TitleSize.H2}>Add New Payment Method</Title>
      <div className={cls.Field}>
        <Label>NAME ON CARD</Label>
        <TextFormInput title="Name on Card" modelKey="cardHolder" {...formProps} />
      </div>
      <div className={cls.Field}>
        <Label>CARD NUMBER</Label>
        <TextFormInput title="Card Number" modelKey="cardNumber" {...formProps} pattern={cardNumberInputPattern} />
      </div>
      <div className={cls.Fields}>
        <div className={cls.Field}>
          <Label>EXPIRATION DATE</Label>
          <TextFormInput
            title="Expiration Date"
            modelKey="expDate"
            {...formProps}
            pattern={expDateInputPattern}
            placeholder="MM/YY"
            autoslash
            inputmode="numeric"
          />
        </div>
        <div className={cls.Field}>
          <Label>SECURITY CODE</Label>
          <TextFormInput title="Security Code" modelKey="cvc" {...formProps} pattern={cvcInputPattern} />
        </div>
      </div>
      <div className={cls.Field}>
        <Label>ZIP CODE</Label>
        <TextFormInput title="Zip Code" modelKey="cardZipcode" {...formProps} pattern={zipCodeGlobalPattern} />
      </div>
      <Button type="submit" isLoading={submittingPayment} disabled={submittingPayment}>
        {(buttonText ?? "Add").toUpperCase()}
      </Button>
    </form>
  );
});
